import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export type FunnelStepTag = 'largest-drop-off' | 'journey-complete';

export interface IFunnelStep {
  /** Position in the journey, printed as the card's `01` eyebrow. */
  index: number;
  name: string;
  /** Pre-formatted by the caller — a stage reads as a count, a rate or a currency amount. */
  value: string;
  /**
   * Level as a fraction of the entry population. The ribbon's taper is drawn from it, and the two
   * stat rows are the ratio of adjacent shares, so a stage without one draws neither.
   */
  share?: number;
  /**
   * The stage's own `Progressed` reading as a 0-1 fraction, when the payload STATES it rather than
   * leaving it to be derived. Supplied where a design's drawn percentages do not reconcile with its
   * drawn counts — Customer 360's Engagement Funnel draws `70.0%` on a stage whose counts compute
   * `128.6%` (`PLA-8828 design/notes.md` 4), and deriving there prints an impossible negative
   * `Dropped`. Absent, the ratio of adjacent shares is used exactly as before. PLA-8828.
   */
  progressed?: number;
  tag?: FunnelStepTag;
}

export interface IFunnelStepChainProps {
  steps: IFunnelStep[];
  /**
   * Fires with the `steps` array position of the stage the transition LEAVES, so `0` is the
   * first-to-second drop. Node 589-30194 draws the control on the stage card the transition lands
   * on, so stage `p` reports `p - 1`; the entry stage has no control and reports nothing.
   */
  onSelectTransition?: (fromIndex: number) => void;
  /**
   * Whether a transition has a breakdown to open, by the same `fromIndex`. Absent, every transition
   * has one; a transition without one draws no control.
   */
  canSelectTransition?: (fromIndex: number) => boolean;
  className?: string;
}

const TAG_LABEL_KEYS: Record<FunnelStepTag, string> = {
  'largest-drop-off': 'LargestDropOff',
  'journey-complete': 'JourneyComplete'
};

const TAG_HUES: Record<FunnelStepTag, string> = {
  'largest-drop-off': tokens.color.negative,
  'journey-complete': tokens.color.positive
};

const TAG_FILL_OPACITY = 0.14;

const CAPTION_INDEX_DIGITS = 2;

/* Node 589-30194 happens to draw seven stage cards on a 152.28px pitch (`span.arrow` lefts 243 /
   395.28 / 547.56 / 699.86 / 852.14 / 1004.42 sit on the midpoints between them), alternating above
   and below a tapering ribbon: odd stages above, even below. SEVEN IS THE MOCK, NOT THE CONTRACT —
   a live journey is configured per tenant, so the column count is the payload's length and every
   position below is a fraction of it. What the file fixes is the RATIO: the card is 172 wide
   against a 155 column, so it fills its column rather than overflowing into its neighbour's. */

/* `Group 40458` draws one `div.funnelShape` per stage — 589-30213 / 30217 / 30221 / 30225 / 30229 /
   30233 / 30237, each 134.28 x 93 on the same 152.28 pitch, so an 18px gap falls between them and
   the 28px `span.arrow` straddles it. The boxes are all the same height and the panel does not
   expose the polygon inside them, so no drawn taper exists to copy — the silhouette below is the
   file's own, and it is fixed rather than read off the payload. */
const RIBBON_HEIGHT = 93;
const SEGMENT_GAP = 18 / 152.28;
const JUNCTION_SIZE = 28;

// The gradient the ribbon and every leader line report (`div.leader` 589-30246 / 30247 / 30353).
const RIBBON_INK = tokens.color['accent-blue'];
const LEADER_HEIGHT = 36;

/* Each step holds its widest caption word and figure: below that the chain scrolls sideways rather
   than spilling text out of its card. PLA-8844. */
const STEP_FLOOR = scaledPx(112);

const Chain = styled.div<{ $columns: number }>`
  display: grid;
  overflow-x: auto;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(${STEP_FLOOR}, 1fr));
  grid-template-rows: auto ${RIBBON_HEIGHT}px auto;
  align-items: stretch;
`;

/* The above row hangs its cards off the ribbon and the below row stands them on it, so each slot
   pushes its own card away from the middle and leaves the leader line against it. */
const Slot = styled.div<{ $above: boolean }>`
  display: flex;
  flex-direction: ${({ $above }) => ($above ? 'column' : 'column-reverse')};
  justify-content: flex-end;
  grid-row: ${({ $above }) => ($above ? 1 : 3)};
  min-width: 0;
  padding: 0 ${tokens.space['4']};
`;

const Card = styled.div`
  padding: ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card-alt']};
`;

// 1px x 36, a fade of the ribbon's own hue — it belongs to the diagram, not to the card.
const Leader = styled.span`
  align-self: center;
  width: 1px;
  height: ${LEADER_HEIGHT}px;
  background: linear-gradient(${RIBBON_INK}, transparent);
`;

const StageNumber = styled.span`
  display: block;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const Caption = styled.span`
  display: block;
  margin-top: ${tokens.space['4']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
`;

const Value = styled.span`
  display: block;
  margin-top: ${tokens.space['8']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['stat-size']};
  font-weight: ${tokens.type['stat-weight']};
  line-height: ${tokens.type['stat-line']};
`;

const StatRow = styled.span`
  display: flex;
  justify-content: space-between;
  margin-top: ${tokens.space['4']};
  font-size: ${tokens.type['label-size']};
  line-height: ${tokens.type['label-line']};
`;

const StatLabel = styled.span`
  color: ${tokens.color['text-secondary']};
`;

const StatValue = styled.span<{ $tone: 'positive' | 'negative' }>`
  color: ${({ $tone }) => tokens.color[$tone]};
  font-weight: ${tokens.type['card-title-weight']};
`;

const Tag = styled.span<{ $tag: FunnelStepTag }>`
  position: relative;
  display: inline-block;
  margin-top: ${tokens.space['8']};
  padding: 0 ${tokens.space['8']};
  border-radius: ${tokens.radius.pill};
  color: ${({ $tag }) => TAG_HUES[$tag]};
  max-width: 100%;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  /* PLA-8844: was nowrap, which painted a 112px badge across a 94px grid step. The step is
     minmax(0, 1fr), so it narrows with the column and the badge has to be allowed to follow it.
     The padding is already a scaled token. */
  overflow-wrap: anywhere;

  /* Tinted fill at 14%, per ConfidenceBadge: a token is a CSS custom-property reference, so the
     alpha cannot be folded into the value and color-mix() is outside this repo's browser floor. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: inherit;
    background: ${({ $tag }) => TAG_HUES[$tag]};
    opacity: ${TAG_FILL_OPACITY};
    pointer-events: none;
  }
`;

/**
 * The tint is an absolutely-positioned `::before`, which paints after the parent's own inline text.
 * Raising the label into the positioned layer — the wrapper `ConfidenceBadge.BadgeText` exists for —
 * puts the wash back behind the words.
 */
const TagText = styled.span`
  position: relative;
`;

/**
 * `View Details` (590-41303 / 41305 / 41307 / 41309 / 41311 / 41313) sits inside the stage card, one
 * per stage after the entry. It is a real `<button>` so it is tab-reachable and operable by
 * keyboard — Safari also never focuses a button on mouse click
 * (conventions/components/cross-browser.md § 1), so nothing here may depend on click-set focus.
 */
const ViewDetails = styled.button`
  display: block;
  margin: ${tokens.space['12']} 0 0 auto;
  padding: 0;
  border: none;
  border-radius: ${tokens.radius.xs};
  background: none;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const RibbonRow = styled.div`
  position: relative;
  grid-row: 2;
  grid-column: 1 / -1;
`;

const Ribbon = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
`;

const Junction = styled.span`
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${JUNCTION_SIZE}px;
  height: ${JUNCTION_SIZE}px;
  margin: ${-JUNCTION_SIZE / 2}px 0 0 ${-JUNCTION_SIZE / 2}px;
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: 50%;
  background: ${tokens.color['surface-card']};
  color: ${RIBBON_INK};
  font-size: ${tokens.type['body-size']};
  line-height: 1;
`;

const captionIndex = (index: number): string =>
  Number.isFinite(index) ? String(index).padStart(CAPTION_INDEX_DIGITS, '0') : '';

const shareOf = (step?: IFunnelStep): number | undefined =>
  step && Number.isFinite(step.share) ? step.share : undefined;

/**
 * The stage cards print one decimal and keep it — `75.0%` / `25.0%` (589-30311 / 589-30316), where
 * the shared `formatSharePercent` drops a trailing zero to `75%`. The pair also has to sum to 100
 * on screen, which a two-way independent round would not guarantee.
 */
const stagePercent = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`;

/**
 * Fraction of the previous stage that reached this one. A stage that STATES its own reading is taken
 * at its word; otherwise it is the ratio of two shares the payload already states rather than a
 * third figure, so the stat rows and the ribbon can never disagree.
 */
export const progressedInto = (steps: IFunnelStep[], position: number): number | undefined => {
  const stated = steps[position]?.progressed;

  if (Number.isFinite(stated)) {
    return stated;
  }

  const previous = shareOf(steps[position - 1]);
  const current = shareOf(steps[position]);

  if (previous === undefined || current === undefined || previous <= 0) {
    return undefined;
  }

  const derived = current / previous;

  // A stage cannot gain population it never had, so a ratio above 1 is not a reading -- it means the
  // two shares are not the entry-relative fractions this derivation assumes. Answering `undefined`
  // draws no figure, where returning it prints `Progressed 187.5%` beside `Dropped -87.5%`. PLA-8828.
  return derived > 1 ? undefined : derived;
};

/* The silhouette is STATIC: a function of position and stage count only, so a journey whose live
   readings happen to be flat still reads as a funnel; the payload's shares keep driving every printed
   figure on the cards, just not the shape. PLA-8844.

   Frame 3583-9763 (the Customer 360 Engagement Funnel, rendered from the same div.wedge mask the
   Customer Insights node 589-30194 uses) draws seven wedges whose entry edges measure 58 / 45 / 36 /
   25 / 15 / 9 / 4 px, which is ((count - position) / count) ^ 1.5 to within 2px, and whose exit edges
   are about 0.6 of their own entry: each wedge narrows, the next opens a little taller than the last
   one closed, and the whole chain converges to a thin line. The floor keeps the last wedge visible
   on a long journey. */
const STAGE_HEIGHT_HEAD = 1;
const STAGE_HEIGHT_CURVE = 1.5;
const STAGE_TAPER = 0.6;
const STAGE_HEIGHT_FLOOR = 0.03;

export const stageHeightFraction = (position: number, count: number): number => {
  if (count <= 1) {
    return STAGE_HEIGHT_HEAD;
  }

  const clamped = Math.min(Math.max(position, 0), count - 1);

  return Math.max(((count - clamped) / count) ** STAGE_HEIGHT_CURVE, STAGE_HEIGHT_FLOOR);
};

export const stageTailFraction = (position: number, count: number): number =>
  Math.max(stageHeightFraction(position, count) * STAGE_TAPER, STAGE_HEIGHT_FLOOR);

/**
 * One tapering segment per stage, in the ribbon's own 100-wide viewBox — a fraction of the payload's
 * own length, so the band tracks however many stages the tenant configured. A segment runs its own
 * stage's entry height down to its own exit height, so every stage, the last included, is a wedge.
 *
 * Every stage draws. The heights come from `stageHeightFraction` rather than from the payload, so a
 * stage the payload gives no share is no longer a hole in the band — the silhouette states nothing
 * about any stage's population and the two stat rows on the card still state everything that is
 * read off the data.
 */
const ribbonSegments = (
  steps: IFunnelStep[]
): { key: string; height: number; tail: number; points: string }[] => {
  const columnWidth = 100 / steps.length;
  const gap = (columnWidth * SEGMENT_GAP) / 2;
  const middle = RIBBON_HEIGHT / 2;

  return steps.map((step, position) => {
    const height = stageHeightFraction(position, steps.length);
    const tail = stageTailFraction(position, steps.length);
    const left = height * middle;
    const right = tail * middle;
    const x0 = columnWidth * position + gap;
    const x1 = columnWidth * (position + 1) - gap;

    return {
      key: `${step.index}-${step.name}`,
      height,
      tail,
      points: [
        `${x0},${middle - left}`,
        `${x1},${middle - right}`,
        `${x1},${middle + right}`,
        `${x0},${middle + left}`
      ].join(' ')
    };
  });
};

const FunnelStepChain: React.FC<IFunnelStepChainProps> = ({
  steps,
  onSelectTransition,
  canSelectTransition,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const stages = Array.isArray(steps) ? steps : [];

  // The FR forbids fabricating a journey, so an empty funnel leaves no chrome behind for the card
  // state block to sit beside.
  if (stages.length === 0) {
    return null;
  }

  const gradientId = 'funnel-ribbon-fill';

  return (
    <Chain
      className={className}
      $columns={stages.length}
      data-columns={stages.length}
      data-testid="funnel-step-chain"
    >
      {stages.map((step, position) => {
        const above = position % 2 === 0;
        const progressed = progressedInto(stages, position);

        return (
          <Slot
            key={`${step.index}-${step.name}`}
            $above={above}
            data-row={above ? 'above' : 'below'}
            data-testid="funnel-step-chain-slot"
            style={{ gridColumn: position + 1 }}
          >
            <Card data-testid="funnel-step-chain-step">
              <StageNumber data-testid="funnel-step-chain-index">
                {captionIndex(step.index)}
              </StageNumber>
              <Caption data-testid="funnel-step-chain-caption">{step.name}</Caption>
              <Value data-testid="funnel-step-chain-value">{step.value}</Value>

              {progressed === undefined ? null : (
                <>
                  <StatRow data-testid="funnel-step-chain-stat">
                    <StatLabel>{t(`${TCustomerInsights}.Funnel.Stats.Progressed`)}</StatLabel>
                    <StatValue $tone="positive">{stagePercent(progressed)}</StatValue>
                  </StatRow>
                  <StatRow data-testid="funnel-step-chain-stat">
                    <StatLabel>{t(`${TCustomerInsights}.Funnel.Stats.Dropped`)}</StatLabel>
                    <StatValue $tone="negative">{stagePercent(1 - progressed)}</StatValue>
                  </StatRow>
                </>
              )}

              {step.tag ? (
                <Tag $tag={step.tag} data-tag={step.tag} data-testid="funnel-step-chain-tag">
                  <TagText>
                    {t(`${TCustomerInsights}.Funnel.Tags.${TAG_LABEL_KEYS[step.tag]}`)}
                  </TagText>
                </Tag>
              ) : null}

              {/* Without a handler the link would be a tab stop that answers nothing, so the entry
                  stage — which has no transition to open — draws none either. */}
              {position > 0 &&
              onSelectTransition &&
              (canSelectTransition?.(position - 1) ?? true) ? (
                <ViewDetails
                  type="button"
                  onClick={() => onSelectTransition(position - 1)}
                  aria-label={t(`${TCustomerInsights}.Actions.ViewDetailsFor`, {
                    label: step.name
                  })}
                  data-from-index={position - 1}
                  data-testid="funnel-step-chain-view-details"
                >
                  {t(`${TCustomerInsights}.Actions.ViewDetails`)}
                </ViewDetails>
              ) : null}
            </Card>

            <Leader aria-hidden="true" data-testid="funnel-step-chain-leader" />
          </Slot>
        );
      })}

      <RibbonRow data-testid="funnel-step-chain-ribbon">
        {/* Decorative, and it has to stay that way to be `aria-hidden`: every figure the band
            encodes is already printed as text on the card above or below it, and nothing here is
            revealed only by pointing at it. That is also why this is a polygon generator rather
            than the recharts `Funnel` the repo already has through `TrendChart` / `DonutChart` —
            recharts would draw the stages as the primary mark, with its own labels and a hover
            tooltip carrying readings a keyboard reader could not reach, and it lays its trapezoids
            out contiguously with no room for the junction badges the file puts between them. */}
        <Ribbon
          viewBox={`0 0 100 ${RIBBON_HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={RIBBON_INK} stopOpacity="0.85" />
              <stop offset="100%" stopColor={RIBBON_INK} stopOpacity="0.25" />
            </linearGradient>
          </defs>
          {ribbonSegments(stages).map(segment => (
            <polygon
              key={`segment-${segment.key}`}
              points={segment.points}
              fill={`url(#${gradientId})`}
              data-stage-height={segment.height.toFixed(4)}
              data-stage-tail={segment.tail.toFixed(4)}
              data-testid="funnel-step-chain-segment"
            />
          ))}
        </Ribbon>

        {stages.slice(1).map((step, transition) => (
          <Junction
            key={`junction-${step.index}-${step.name}`}
            aria-hidden="true"
            data-testid="funnel-step-chain-junction"
            style={{ left: `${(100 / stages.length) * (transition + 1)}%` }}
          >
            &#8594;
          </Junction>
        ))}
      </RibbonRow>
    </Chain>
  );
};

export default FunnelStepChain;
